import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router';

import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import {
    faChevronRight,
    faPhoneAlt,
    faRightFromBracket,
    faStar,
    faTimes,
    faTrashAlt,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    Alert,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import DrawerFooter from './DrawerFooter';
import { DrawerComponentProps } from './DrawerManager';
import DrawerTitle from './DrawerTitle';
import { ObjectTypes, useAnalytics } from 'src/AnalyticsContext';
import Groups from 'src/constants/Groups';
import useCareTypeFeatures from 'src/hooks/useCareTypeFeatures';
import useCareZones from 'src/hooks/useCareZones';
import useUserPermissions from 'src/hooks/useUserPermissions';
import {
    selectFacilityId,
    selectIsUserInSageSupportGroup,
    selectStaffMemberId,
} from 'src/store/configSlice';
import {
    Group,
    StaffMember,
    StaffMemberFacilityConfig,
    useGetCompanyPhoneQuery,
    useGetMembershipDetailsQuery,
    useGetStaffMemberFacilityMetadataQuery,
    useGetStaffMemberQuery,
    useRemoveMemberFromGroupMutation,
    useUpdateGroupMembershipMutation,
    useUpdateOnCallStateMutation,
    useUpdateStaffMemberFacilityConfigMutation,
} from 'src/store/sageAdminApi';
import { sageBaseAdminApi } from 'src/store/sageBaseAdminApi';
import { snackActions } from 'src/utils/snackBarTools';
import { getStaffMemberName } from 'src/utils/staffUtils';

export interface StaffMemberActionsDrawerProps extends DrawerComponentProps {
    staffMember: StaffMember;
    group?: Group;
}

function StaffMemberActionsDrawer(props: StaffMemberActionsDrawerProps) {
    const { onNext, onClose, staffMember: staffMemberFromProps, group } = props;

    const { name: groupName, id: groupId = '' } = group || {};

    const { trackEvent } = useAnalytics();
    const dispatch = useDispatch();
    const { shape } = useTheme();

    const facilityId = useSelector(selectFacilityId);
    const currentUserProfileId = useSelector(selectStaffMemberId);
    const userIsInSageSupportGroup = useSelector(selectIsUserInSageSupportGroup);

    const { getFeatures } = useCareTypeFeatures();
    const { userCareTypes } = useCareZones();
    const isEhrTaskingEnabled = userCareTypes.some(
        (careType) => getFeatures(careType).ehr.isTaskingEnabled
    );

    const [showConfirmRemovalDialog, setShowConfirmRemovalDialog] = useState(false);
    const [showConfirmTakeOffShiftDialog, setShowConfirmTakeOffShiftDialog] = useState(false);

    const { canSeePII: userCanSeePII, isManagementGroupAdmin: currentUserIsManagementAdmin } =
        useUserPermissions();

    const { data: staffMemberFromServer, isLoading: isLoadingStaffMember } = useGetStaffMemberQuery(
        { staffMemberId: staffMemberFromProps.id ?? '' },
        { skip: !staffMemberFromProps.id }
    );

    const staffMember = staffMemberFromServer || staffMemberFromProps;
    const staffMemberId = staffMember?.id;

    const staffMemberFullName = getStaffMemberName(staffMember);

    const { data: staffFacilityMetadata, isLoading: isLoadingStaffMemberGroups } =
        useGetStaffMemberFacilityMetadataQuery(
            { staffMemberId: staffMemberId!, facilityId },
            { skip: !facilityId || !staffMemberId }
        );

    const staffMemberGroups = useMemo(
        () =>
            staffFacilityMetadata?.find((facility) => facility.facilityId === facilityId)?.groups ||
            [],
        [facilityId, staffFacilityMetadata]
    );

    const staffMemberIsOnCallAtFacility = useMemo(
        () =>
            (staffMember?.onCallStates || []).some(
                (state) => state.facilityId === facilityId && state.isOnCall
            ),
        [facilityId, staffMember]
    );

    const taskGroupId = useMemo(
        () => groupId || staffFacilityMetadata?.[0]?.groups?.[0].groupId,
        [groupId, staffFacilityMetadata]
    );

    const facilityOnCallState = useMemo(
        () => staffMember?.onCallStates?.find((state) => state.facilityId === facilityId),
        [facilityId, staffMember]
    );

    const { data: companyPhone, isLoading: isLoadingCompanyPhone } = useGetCompanyPhoneQuery(
        { companyPhoneId: facilityOnCallState?.companyPhoneId ?? '' },
        { skip: !facilityOnCallState || !facilityOnCallState.companyPhoneId }
    );

    const isUsingCompanyPhone = facilityOnCallState?.isOnCall && !!companyPhone;

    const { data: groupMembers, isLoading: isLoadingGroupMembers } = useGetMembershipDetailsQuery(
        { groupId },
        { skip: !groupId }
    );

    const groupAdmins = useMemo(
        () => (groupMembers ?? []).filter((member) => member.isAdmin),
        [groupMembers]
    );

    const staffMemberIsGroupAdmin = useMemo(
        () => groupAdmins.some((member) => member.staffMember?.id === staffMemberId),
        [groupAdmins, staffMemberId]
    );

    const currentUserIsGroupAdmin = useMemo(
        () => groupAdmins.some((member) => member.staffMember?.id === currentUserProfileId),
        [groupAdmins, currentUserProfileId]
    );

    // Current user properties
    const canModifyGroupMembership = currentUserIsManagementAdmin || currentUserIsGroupAdmin;
    const canRemoveAsAdmin = canModifyGroupMembership && (groupAdmins?.length || 0) > 1;
    const canTakeOffShift = currentUserIsManagementAdmin && userIsInSageSupportGroup;

    // Viewed user properties
    const isMemberOfOnlyOneGroup = staffMemberGroups.length === 1;
    const isUserViewingSelf = staffMemberId === currentUserProfileId;

    const handleTrackButtonClick = (eventName: string) => {
        trackEvent(eventName, { objectType: ObjectTypes.Button });
        onNext();
    };

    const [removeMemberFromGroup, { isLoading: isRemovingMemberFromGroup }] =
        useRemoveMemberFromGroupMutation();
    const [updateGroupMembership, { isLoading: isUpdatingGroupMembership }] =
        useUpdateGroupMembershipMutation();
    const [updateStaffMemberFacilityConfig, { isLoading: isUpdatingStaffMember }] =
        useUpdateStaffMemberFacilityConfigMutation();
    const [updateStaffMemberOnCallStatus, { isLoading: isUpdatingOnCallStatus }] =
        useUpdateOnCallStateMutation();

    const handleMakeGroupAdmin = async () => {
        trackEvent('Make Group Admin Clicked', {
            groupName: groupName,
            newAdminStatus: !staffMemberIsGroupAdmin,
        });

        try {
            await updateGroupMembership({
                groupId,
                createOrUpdateGroupMembershipRequest: { staffMemberId, isAdmin: true },
            }).unwrap();

            snackActions.success(`${staffMemberFullName} was made a ${groupName} group admin`);

            onNext();
        } catch (error) {
            datadogRum.addError(error, {
                message: 'Failed to make group admin',
                groupName,
                groupId,
                staffMemberId,
            });
            snackActions.error(`Failed to make ${staffMemberFullName} a ${groupName} group admin`);
        }
    };

    const handleDismissAsGroupAdmin = async () => {
        trackEvent('Dismiss As Group Admin Clicked', {
            groupName: groupName,
            newAdminStatus: !staffMemberIsGroupAdmin,
        });

        try {
            await updateGroupMembership({
                groupId,
                createOrUpdateGroupMembershipRequest: { staffMemberId, isAdmin: false },
            }).unwrap();

            snackActions.success(`${staffMemberFullName} is no longer a ${groupName} admin`);

            onNext();
        } catch (error) {
            datadogRum.addError(error, {
                message: 'Failed to toggle group admin status',
                groupName,
                groupId,
                staffMemberId,
            });
            snackActions.error(
                `Failed to dismiss ${staffMemberFullName} as a ${groupName} group admin`
            );
        }
    };

    const handleRemoveTeamMemberFromGroup = () => {
        trackEvent('Remove Team Member Clicked', { groupName: groupName });
        setShowConfirmRemovalDialog(true);
    };

    const handleConfirmRemoveTeamMemberFromGroup = async () => {
        trackEvent('Confirm Remove Team Clicked', { groupName: groupName });

        setShowConfirmRemovalDialog(false);

        if (!staffMember) {
            datadogLogs.logger.warn('Attempting to remove no profile!', {
                groupName: groupName,
                groupId: group?.id,
            });
            return;
        }

        const { firstName, lastName, id } = staffMember;

        try {
            // Update the staff member first before removing them from the group to avoid
            // the case where a staff member is removed from all groups in the facility and
            // the user no longer has permission to update the removed staff member profile.
            if (groupName === Groups.CareStaff) {
                const staffMemberFacilityConfig = staffMember.staffMemberFacilityConfigs?.find(
                    (config: StaffMemberFacilityConfig) => config.facilityId === facilityId
                );

                if (staffMemberFacilityConfig) {
                    await updateStaffMemberFacilityConfig({
                        staffMemberFacilityConfigId: staffMemberFacilityConfig.id!,
                        createOrUpdateStaffMemberFacilityConfigRequest: {
                            shouldReceiveAllCareAlerts: false,
                            shouldReceiveAllCareMessages: false,
                        },
                    });
                }
            }

            if (!!groupId && !!id) {
                await removeMemberFromGroup({ groupId, staffMemberId: id }).unwrap();
                dispatch(sageBaseAdminApi.util.invalidateTags(['staff']));

                snackActions.success(`${firstName} ${lastName} was removed from ${groupName}`);
                onNext();
            }
        } catch (error) {
            datadogRum.addError(error, {
                message: 'Failed to remove staff member from group',
                groupName,
                groupId,
                staffMemberId: id,
            });
            snackActions.error(`Failed to remove ${firstName} ${lastName} from ${groupName}`);
        }
    };

    const handleCancelRemoveTeamMemberFromGroup = () => {
        trackEvent('Cancel Remove Team Clicked', { groupName });
        setShowConfirmRemovalDialog(false);
    };

    const handleTakeOffShift = () => {
        trackEvent('Take Off Shift Clicked', { groupName });
        setShowConfirmTakeOffShiftDialog(true);
    };

    const handleConfirmTakeOffShift = async () => {
        trackEvent('Take Off Shift Confirmed', { groupName });

        if (staffMemberId) {
            try {
                await updateStaffMemberOnCallStatus({
                    staffMemberId,
                    facilityId,
                    updateOnCallStateRequest: { isOnCall: false },
                }).unwrap();

                snackActions.success(`${staffMemberFullName} has been taken off shift`);
                onNext();
            } catch (error) {
                datadogRum.addError(error, {
                    message: 'Failed to take off shift',
                    groupName,
                    groupId,
                    staffMemberId,
                });
                snackActions.error(`Failed to take ${staffMemberFullName} off shift`);
            }
        } else {
            datadogLogs.logger.warn('Attempting to update call status of unknown staff member!', {
                groupName: groupName,
                staffMember: staffMember,
            });
            return;
        }
    };

    const handleCancelTakeOffShift = () => {
        trackEvent('Take Off Shift Cancelled', { groupName });
        setShowConfirmTakeOffShiftDialog(false);
    };

    const isLoading =
        isLoadingStaffMember ||
        isLoadingStaffMemberGroups ||
        isLoadingGroupMembers ||
        isLoadingCompanyPhone;

    const shouldDisableAllActions =
        isLoading ||
        isRemovingMemberFromGroup ||
        isUpdatingGroupMembership ||
        isUpdatingStaffMember;

    return (
        <>
            <DrawerTitle title={staffMemberFullName} />

            <DrawerFooter sx={{ gap: 2 }}>
                {staffMemberId && !isEhrTaskingEnabled && (
                    <Button
                        color="primary"
                        component={Link}
                        disabled={shouldDisableAllActions}
                        disableElevation
                        endIcon={<FontAwesomeIcon icon={faChevronRight} />}
                        fullWidth
                        onClick={() => handleTrackButtonClick('Assign Task Clicked')}
                        state={{ assigneeId: staffMemberId, groupId: taskGroupId }}
                        to="/tasks/add"
                        variant="contained"
                    >
                        Assign task
                    </Button>
                )}

                {isUsingCompanyPhone ? (
                    <Button
                        color="primary"
                        disableElevation
                        disabled={!companyPhone?.phoneNumber}
                        fullWidth
                        href={`tel:${companyPhone?.phoneNumber}`}
                        onClick={() => handleTrackButtonClick('Call Company Phone Clicked')}
                        endIcon={<FontAwesomeIcon icon={faPhoneAlt} />}
                        variant="outlined"
                    >
                        Call ({companyPhone.displayName})
                    </Button>
                ) : (
                    userCanSeePII && (
                        <Button
                            color="primary"
                            disableElevation
                            disabled={!staffMember?.phoneNumber}
                            fullWidth
                            href={`tel:${staffMember?.phoneNumber}`}
                            onClick={() => handleTrackButtonClick('Call Personal Phone Clicked')}
                            endIcon={<FontAwesomeIcon icon={faPhoneAlt} />}
                            variant="outlined"
                        >
                            Call Personal Phone
                        </Button>
                    )
                )}

                <Button
                    color="primary"
                    component={Link}
                    disableElevation
                    disabled={shouldDisableAllActions || !staffMember?.id}
                    endIcon={<FontAwesomeIcon icon={faChevronRight} />}
                    fullWidth
                    onClick={() => handleTrackButtonClick('View Profile Clicked')}
                    to={`/team/member/${staffMember?.id}`}
                    variant="outlined"
                >
                    View Profile
                </Button>

                {group &&
                    canModifyGroupMembership &&
                    (!staffMemberIsGroupAdmin ? (
                        <Button
                            color="primary"
                            disableElevation
                            disabled={shouldDisableAllActions}
                            endIcon={<FontAwesomeIcon icon={faStar} />}
                            fullWidth
                            loading={isUpdatingGroupMembership}
                            onClick={handleMakeGroupAdmin}
                            variant="outlined"
                        >
                            Make Group Admin
                        </Button>
                    ) : (
                        canRemoveAsAdmin && (
                            <Button
                                color="primary"
                                disableElevation
                                disabled={shouldDisableAllActions}
                                endIcon={<FontAwesomeIcon icon={faTimes} />}
                                fullWidth
                                loading={isUpdatingGroupMembership}
                                onClick={handleDismissAsGroupAdmin}
                                variant="outlined"
                            >
                                Dismiss as Group Admin
                            </Button>
                        )
                    ))}

                {group && canModifyGroupMembership && (
                    <>
                        <Button
                            color="error"
                            disabled={shouldDisableAllActions || isMemberOfOnlyOneGroup}
                            disableElevation
                            endIcon={<FontAwesomeIcon icon={faTrashAlt} />}
                            fullWidth
                            loading={isRemovingMemberFromGroup}
                            onClick={handleRemoveTeamMemberFromGroup}
                            variant="outlined"
                        >
                            {isUserViewingSelf ? 'Remove Me' : 'Remove Team Member'}
                        </Button>

                        {isMemberOfOnlyOneGroup && (
                            <Alert
                                severity="info"
                                variant="filled"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    borderRadius: shape.borderRadiusMd,
                                    backgroundColor: 'neutral.50',
                                }}
                            >
                                <Typography variant="body2">
                                    Team members must be in at least one group.
                                </Typography>
                            </Alert>
                        )}
                    </>
                )}

                {canTakeOffShift && staffMemberIsOnCallAtFacility && (
                    <Button
                        color="error"
                        disableElevation
                        endIcon={<FontAwesomeIcon icon={faRightFromBracket} />}
                        fullWidth
                        loading={isUpdatingOnCallStatus}
                        onClick={handleTakeOffShift}
                        variant="outlined"
                        sx={{ textTransform: 'capitalize' }}
                    >
                        Take {isUserViewingSelf ? 'Me' : staffMember?.firstName} Off Shift
                    </Button>
                )}

                <Button color="primary" disableElevation fullWidth onClick={onClose}>
                    Cancel
                </Button>
            </DrawerFooter>

            <Dialog open={showConfirmTakeOffShiftDialog} onClose={handleCancelTakeOffShift}>
                <DialogTitle>
                    Taking <b>{staffMemberFullName}</b> off shift will stop them from receiving
                    alerts.
                </DialogTitle>

                <DialogContent>
                    <Stack sx={{ gap: 4 }}>
                        <Typography variant="h4">Are you sure you want to continue?</Typography>

                        <Stack sx={{ gap: 2 }}>
                            <Button
                                variant="outlined"
                                color="error"
                                disableElevation
                                onClick={handleConfirmTakeOffShift}
                            >
                                Continue
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                disableElevation
                                onClick={handleCancelTakeOffShift}
                            >
                                Cancel
                            </Button>
                        </Stack>
                    </Stack>
                </DialogContent>
            </Dialog>

            <Dialog open={showConfirmRemovalDialog} onClose={handleCancelRemoveTeamMemberFromGroup}>
                <DialogTitle>
                    <Typography variant="h4">
                        Are you sure you want to remove <b>{staffMemberFullName}</b> from the{' '}
                        {groupName} group?
                    </Typography>
                </DialogTitle>

                <DialogContent>
                    <Stack sx={{ gap: 2 }}>
                        <Button
                            color="primary"
                            disableElevation
                            fullWidth
                            onClick={handleConfirmRemoveTeamMemberFromGroup}
                            variant="outlined"
                        >
                            Yes
                        </Button>
                        <Button
                            color="primary"
                            disableElevation
                            fullWidth
                            onClick={handleCancelRemoveTeamMemberFromGroup}
                            variant="contained"
                        >
                            No
                        </Button>
                    </Stack>
                </DialogContent>
            </Dialog>
        </>
    );
}

export default StaffMemberActionsDrawer;
